import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type DriverStreak } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, StatTile, type Column } from '@/components/ui';
import { IconAward } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';

const PAGE = 25;

/** Streaks — the streak-bonus rules a driver earns by completing N trips in a
 *  named zone during a time window (optionally constrained to one weekday).
 *  incentives-svc reads the `driver_streaks` table at request time to build the
 *  driver app's Opportunities "Streak" card (it picks the first active matching
 *  rule → required_trips, bonus_amount, zone, time-window). incentives-svc OWNS
 *  that table.
 *
 *  READ-ONLY here — by design, for now. incentives-svc exposes only the
 *  driver-facing read of streak rules; it has NO internal admin endpoint to
 *  create / edit / delete a streak rule, so there is nothing for admin-svc to
 *  proxy a write to (and admin-svc never writes a table another service owns).
 *  This is a clean list + detail VIEW (no dead write buttons). Full CRUD lights
 *  up the day incentives-svc grows the authoring endpoints (it already proxies
 *  campaign + promo writes the same way) — see the read-only banner. The section
 *  is DEFENSIVE (shows a "not yet available" state when the table isn't in this
 *  Postgres). Covers the legacy Filament DriverStreakResource (its columns + the
 *  Active/Inactive status badge). */
export function Streaks() {
  const [q, setQ] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<DriverStreak | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'streaks', q, activeOnly, offset],
    queryFn: ({ signal }) =>
      adminApi.streaks({ q, active: activeOnly ? 1 : undefined, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const unavailable = data && data.available === false;

  // Glance figures over the CURRENT page (there is no dedicated stats endpoint —
  // the legacy resource had none either; this mirrors the table at a glance).
  const pageStats = useMemo(() => {
    const rows = data?.items ?? [];
    const active = rows.filter((r) => r.status === 1);
    const maxBonus = active.reduce((m, r) => Math.max(m, r.bonus_amount), 0);
    return { active: active.length, maxBonus };
  }, [data]);

  const columns: Array<Column<DriverStreak>> = [
    { key: 'id', header: 'ID', cell: (r) => <span className="metric text-xs text-muted">#{r.id}</span> },
    {
      key: 'trips',
      header: 'Trips',
      align: 'right',
      cell: (r) => <span className="metric text-sm text-slate-100">{r.required_trips}</span>,
    },
    {
      key: 'bonus',
      header: 'Bonus',
      align: 'right',
      cell: (r) => <span className="metric text-sm text-ok">+${r.bonus_amount.toFixed(2)}</span>,
    },
    {
      key: 'zone',
      header: 'Zone',
      cell: (r) =>
        r.zone_name ? (
          <span className="font-medium text-slate-100">{r.zone_name}</span>
        ) : (
          <span className="text-xs text-muted">All zones</span>
        ),
    },
    {
      key: 'window',
      header: 'Time window',
      cell: (r) => <span className="metric text-xs text-slate-300">{r.time_window}</span>,
    },
    {
      key: 'day',
      header: 'Day',
      cell: (r) => <span className="text-xs text-slate-300">{r.day_name}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <Badge tone={r.status === 1 ? 'ok' : 'neutral'} dot>
          {r.status === 1 ? 'active' : 'inactive'}
        </Badge>
      ),
    },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          View
        </button>
      ),
    },
  ];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Streaks"
        subtitle="Streak bonuses drivers earn by completing N trips in a zone + time window."
        right={
          <SearchBox
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search by zone…"
          />
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Streaks not yet in Postgres"
            description={
              data?.note ??
              'The driver_streaks table is owned by incentives-svc and is not in this Postgres yet.'
            }
            icon={<IconAward className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          {/* Stat tiles — glance figures over the streak ruleset (this page). */}
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <StatTile
              label="Rules"
              value={data?.total ?? '—'}
              icon={<IconAward className="h-4 w-4" />}
              hint="total streak-bonus rules"
            />
            <StatTile label="Active (this page)" value={data ? pageStats.active : '—'} tone="ok" />
            <StatTile
              label="Top bonus (this page)"
              value={data ? `$${pageStats.maxBonus.toFixed(2)}` : '—'}
              tone="info"
            />
          </div>

          {/* Read-only notice — this surface is intentionally a view (no authoring
              endpoint exists upstream yet). Keeps the page honest: no dead buttons. */}
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-ink-800 bg-ink-850 px-3 py-2 text-xs text-text-secondary">
            <IconAward className="mt-0.5 h-4 w-4 shrink-0 text-brand-400" />
            <span>
              Read-only view. Streak bonuses are authored upstream in incentives-svc; create / edit / delete will appear
              here once incentives-svc exposes its internal streak-authoring endpoints.
            </span>
          </div>

          <div className="mb-3 flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => {
                  setActiveOnly(e.target.checked);
                  setOffset(0);
                }}
                className="h-4 w-4 rounded border-ink-700 bg-ink-850 text-brand-500 focus:ring-brand-500"
              />
              Active only
            </label>
          </div>

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading streaks…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load streaks" description={(error as Error)?.message} />
            ) : (
              <>
                <DataTable<DriverStreak>
                  columns={columns}
                  rows={data?.items ?? []}
                  rowKey={(r) => String(r.id)}
                  stickyHeader
                  empty={
                    <EmptyState
                      title="No streaks"
                      description={
                        activeOnly || q
                          ? 'No streak bonuses match this filter.'
                          : 'No streak bonuses configured yet.'
                      }
                      icon={<IconAward className="h-6 w-6" />}
                    />
                  }
                />
                <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
              </>
            )}
          </Card>
        </>
      )}

      {selected !== null ? <StreakDrawer streak={selected} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}

// ─── streak detail drawer (read-only) ────────────────────────

/** StreakDrawer — the read-only detail panel for one streak rule. Seeded directly
 *  from the row (no extra fetch needed — the list already carries the full row),
 *  so it opens instantly. Purely informational, matching the read-only surface. */
function StreakDrawer({ streak, onClose }: { streak: DriverStreak; onClose: () => void }) {
  return (
    <Drawer title={`Streak #${streak.id}`} subtitle={streak.zone_name || 'All zones'} onClose={onClose}>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="metric text-2xl font-semibold text-ok">+${streak.bonus_amount.toFixed(2)}</span>
          <Badge tone={streak.status === 1 ? 'ok' : 'neutral'} dot>
            {streak.status === 1 ? 'active' : 'inactive'}
          </Badge>
        </div>

        <p className="text-[13px] text-text-secondary">
          Drivers earn this bonus after completing <span className="font-medium text-slate-100">{streak.required_trips}</span>{' '}
          {streak.required_trips === 1 ? 'trip' : 'trips'}
          {streak.zone_name ? (
            <>
              {' '}
              in <span className="font-medium text-slate-100">{streak.zone_name}</span>
            </>
          ) : null}{' '}
          on <span className="font-medium text-slate-100">{streak.day_name.toLowerCase()}</span>
          {streak.time_window && streak.time_window !== 'Any time' ? (
            <>
              {' '}
              between <span className="metric text-slate-100">{streak.time_window}</span>
            </>
          ) : null}
          .
        </p>

        <Card>
          <div>
            <Field label="ID" value={`#${streak.id}`} />
            <Field label="Required trips" value={String(streak.required_trips)} />
            <Field label="Bonus" value={`$${streak.bonus_amount.toFixed(2)}`} />
            <Field label="Zone" value={streak.zone_name || 'All zones'} />
            <Field label="Day" value={streak.day_name} />
            <Field label="Time window" value={streak.time_window} />
            <Field label="Status" value={streak.status === 1 ? 'Active' : 'Inactive'} />
          </div>
        </Card>

        <p className="text-[11px] text-text-muted">
          Read-only. This streak rule is authored in incentives-svc; it surfaces on the driver app's Opportunities screen
          while active.
        </p>
      </div>
    </Drawer>
  );
}
